import { useState, useMemo, useEffect } from 'react'
import type { BundleItem } from '../api/types'
import type { CounterDenomination } from '@abcpay/shared'

interface KrwDenominationModalProps {
  isOpen: boolean
  initialBundles?: BundleItem[]
  initialLooseTotal?: number
  initialLooseBillCounts?: Record<number, string>
  initialLooseCoinCounts?: Record<number, string>
  externalDenominations?: CounterDenomination[]
  onConfirm: (
    bundles: BundleItem[],
    looseTotal: number,
    total: number,
    looseBillCounts: Record<number, string>,
    looseCoinCounts: Record<number, string>
  ) => void
  onCancel: () => void
}

const BILL_DENOMINATIONS = [50000, 10000, 5000, 1000] as const
const COIN_DENOMINATIONS = [500, 100] as const
const BILL_BUNDLE_COUNT = 100

const COIN_BUNDLE_COUNTS: Record<number, number> = {
  500: 40,
  100: 50,
}

function KrwDenominationModal({
  isOpen,
  initialBundles = [],
  initialLooseBillCounts: initBillCounts,
  initialLooseCoinCounts: initCoinCounts,
  externalDenominations,
  onConfirm,
  onCancel,
}: KrwDenominationModalProps) {
  const [bundleCounts, setBundleCounts] = useState<Record<number, string>>({
    50000: '',
    10000: '',
    5000: '',
    1000: '',
    500: '',
    100: '',
  })

  const [looseBillCounts, setLooseBillCounts] = useState<Record<number, string>>({
    50000: '',
    10000: '',
    5000: '',
    1000: '',
  })

  const [looseCoinCounts, setLooseCoinCounts] = useState<Record<number, string>>({
    500: '',
    100: '',
  })

  useEffect(() => {
    if (isOpen) {
      const newBundleCounts: Record<number, string> = {
        50000: '',
        10000: '',
        5000: '',
        1000: '',
        500: '',
        100: '',
      }
      initialBundles.forEach((bundle) => {
        if (bundle.bundleCount > 0) {
          newBundleCounts[bundle.denomination] = bundle.bundleCount.toString()
        }
      })
      setBundleCounts(newBundleCounts)

      if (externalDenominations && externalDenominations.length > 0) {
        const newLooseBillCounts: Record<number, string> = {
          ...(initBillCounts ?? { 50000: '', 10000: '', 5000: '', 1000: '' }),
        }
        const newLooseCoinCounts: Record<number, string> = {
          ...(initCoinCounts ?? { 500: '', 100: '' }),
        }

        externalDenominations.forEach(({ denomination, count }) => {
          if (BILL_DENOMINATIONS.includes(denomination as (typeof BILL_DENOMINATIONS)[number])) {
            const existingCount = parseInt(newLooseBillCounts[denomination] || '0', 10) || 0
            newLooseBillCounts[denomination] = (existingCount + count).toString()
          } else if (COIN_DENOMINATIONS.includes(denomination as (typeof COIN_DENOMINATIONS)[number])) {
            const existingCount = parseInt(newLooseCoinCounts[denomination] || '0', 10) || 0
            newLooseCoinCounts[denomination] = (existingCount + count).toString()
          }
        })

        setLooseBillCounts(newLooseBillCounts)
        setLooseCoinCounts(newLooseCoinCounts)
      } else {
        setLooseBillCounts(initBillCounts ?? { 50000: '', 10000: '', 5000: '', 1000: '' })
        setLooseCoinCounts(initCoinCounts ?? { 500: '', 100: '' })
      }
    }
  }, [isOpen, initialBundles, initBillCounts, initCoinCounts, externalDenominations])

  const { looseBillTotal, looseCoinTotal, grandTotal } = useMemo(() => {
    let bundleSum = 0
    let looseBillSum = 0
    let looseCoinSum = 0

    BILL_DENOMINATIONS.forEach((denom) => {
      const count = parseInt(bundleCounts[denom] || '0', 10) || 0
      bundleSum += denom * count * BILL_BUNDLE_COUNT
    })
    COIN_DENOMINATIONS.forEach((denom) => {
      const count = parseInt(bundleCounts[denom] || '0', 10) || 0
      bundleSum += denom * count * COIN_BUNDLE_COUNTS[denom]
    })

    BILL_DENOMINATIONS.forEach((denom) => {
      const count = parseInt(looseBillCounts[denom] || '0', 10) || 0
      looseBillSum += denom * count
    })

    COIN_DENOMINATIONS.forEach((denom) => {
      const count = parseInt(looseCoinCounts[denom] || '0', 10) || 0
      looseCoinSum += denom * count
    })

    return {
      bundleTotal: bundleSum,
      looseBillTotal: looseBillSum,
      looseCoinTotal: looseCoinSum,
      grandTotal: bundleSum + looseBillSum + looseCoinSum,
    }
  }, [bundleCounts, looseBillCounts, looseCoinCounts])

  const handleConfirm = () => {
    const bundles: BundleItem[] = []
    ;[...BILL_DENOMINATIONS, ...COIN_DENOMINATIONS].forEach((denom) => {
      const count = parseInt(bundleCounts[denom] || '0', 10) || 0
      if (count > 0) {
        bundles.push({ denomination: denom, bundleCount: count })
      }
    })

    const looseTotal = looseBillTotal + looseCoinTotal
    onConfirm(bundles, looseTotal, grandTotal, looseBillCounts, looseCoinCounts)
  }

  const formatNumber = (value: number): string => {
    return value.toLocaleString('ko-KR')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-fx-text-dark">원화 권종별 계수</h2>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-6">
          <div>
            <h3 className="text-sm font-medium text-fx-text-muted mb-3">
              지폐 묶음 <span className="text-gray-400">(100장)</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {BILL_DENOMINATIONS.map((denom) => (
                <div key={`bundle-${denom}`} className="flex items-center gap-2">
                  <span className="text-sm font-medium w-16 text-right">{formatNumber(denom)}원</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={bundleCounts[denom]}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '')
                      setBundleCounts((prev) => ({ ...prev, [denom]: val }))
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-fx-buy focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-500">묶음</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-fx-text-muted mb-3">
              동전 묶음 <span className="text-gray-400">(한국 은행권 표준)</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {COIN_DENOMINATIONS.map((denom) => (
                <div key={`bundle-${denom}`} className="flex items-center gap-2">
                  <span className="text-sm font-medium w-16 text-right">{formatNumber(denom)}원</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={bundleCounts[denom]}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '')
                      setBundleCounts((prev) => ({ ...prev, [denom]: val }))
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-fx-buy focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-500">
                    묶음 <span className="text-xs text-gray-400">({COIN_BUNDLE_COUNTS[denom]}개)</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-fx-text-muted mb-3">단권</h3>

            <div className="mb-4">
              <p className="text-xs text-gray-400 mb-2">지폐</p>
              <div className="grid grid-cols-2 gap-3">
                {BILL_DENOMINATIONS.map((denom) => (
                  <div key={`loose-bill-${denom}`} className="flex items-center gap-2">
                    <span className="text-sm font-medium w-16 text-right">{formatNumber(denom)}원</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={looseBillCounts[denom]}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '')
                        setLooseBillCounts((prev) => ({ ...prev, [denom]: val }))
                      }}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-fx-buy focus:border-transparent"
                      placeholder="개수"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-400 mb-2">동전</p>
              <div className="grid grid-cols-2 gap-3">
                {COIN_DENOMINATIONS.map((denom) => (
                  <div key={`loose-coin-${denom}`} className="flex items-center gap-2">
                    <span className="text-sm font-medium w-16 text-right">{formatNumber(denom)}원</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={looseCoinCounts[denom]}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '')
                        setLooseCoinCounts((prev) => ({ ...prev, [denom]: val }))
                      }}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-fx-buy focus:border-transparent"
                      placeholder="개수"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-fx-text-dark">원화 총액:</span>
              <span className="text-2xl font-bold text-fx-buy">₩ {formatNumber(grandTotal)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t sticky bottom-0 bg-white">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-fx-text-dark font-medium hover:bg-gray-50 transition-all"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-fx-buy text-white rounded-lg font-medium hover:opacity-90 transition-all"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  )
}

export default KrwDenominationModal
